import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Footnote} from "@khanacademy/wonder-blocks-typography";

/* As designed */
const DISCLAIMER_HEIGHT = 32;
const BORDER_HEIGHT = 1;

interface Props {
    style?: StyleType;
    usePersonalInfoDisclaimer?: boolean;
}

/**
 * A small box that displays above the chat input and warns the user about
 * the limitations of the guide.
 */
const GuideDisclaimer = ({
    style,
    usePersonalInfoDisclaimer,
}: Props): React.ReactElement => {
    const khanmigoForAdminsEnabled = useFeatureIsOn("khanmigo-for-admins");
    return (
        <View style={[styles.disclaimer, style]}>
            <Footnote>
                {usePersonalInfoDisclaimer && khanmigoForAdminsEnabled ? (
                    <Trans>
                        <Link
                            href={t`https://support.khanacademy.org/hc/articles/13888935335309`}
                            target="_blank"
                        >
                            Double check for accuracy and avoid sharing personal
                            info with Khanmigo.
                        </Link>
                    </Trans>
                ) : (
                    <Trans>
                        Khanmigo makes mistakes sometimes.{" "}
                        <Link
                            href={t`https://support.khanacademy.org/hc/articles/13888935335309`}
                            target="_blank"
                        >
                            Here's why
                        </Link>
                        .
                    </Trans>
                )}
            </Footnote>
        </View>
    );
};

const styles = StyleSheet.create({
    disclaimer: {
        height: DISCLAIMER_HEIGHT,
        flexShrink: 0,
        backgroundColor: semanticColor.core.background.base.default,
        padding: spacing.xSmall_8,
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        justifyItems: "center",
        borderBlockEnd: `${BORDER_HEIGHT}px solid ${semanticColor.core.border.neutral.subtle}`,
        borderStartStartRadius: 8,
        borderStartEndRadius: 8,
        color: semanticColor.core.foreground.neutral.subtle,
    },
});

export default GuideDisclaimer;
